(() => {
  "use strict";

  const root = document.documentElement;
  let latestCartSnapshot = null;
  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || "ZAR";
    const value = Number(cents || 0) / 100;
    if (currency === "ZAR") {
      return `R ${value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    try {
      return new Intl.NumberFormat("en-ZA", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (_error) {
      return `R${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const routesRoot = () => window.Shopify?.routes?.root || "/";

  const toast = (message, isError = false) => {
    const element = document.querySelector("[data-ug-toast]");
    if (!element) return;
    element.textContent = message;
    element.classList.toggle("is-error", isError);
    element.classList.add("is-visible");
    window.clearTimeout(element._ugTimer);
    element._ugTimer = window.setTimeout(() => {
      element.classList.remove("is-visible", "is-error");
    }, 4200);
  };

  const getCart = async () => {
    const response = await fetch(`${routesRoot()}cart.js`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Your cart could not be refreshed.");
    return response.json();
  };

  const syncCartCount = (cart) => {
    const count = Number(cart?.item_count);
    if (!Number.isInteger(count) || count < 0) return;
    document.querySelectorAll("[data-ug-cart-count]").forEach((node) => {
      if (node.textContent !== String(count)) node.textContent = String(count);
      node.hidden = count < 1;
    });
    root.classList.toggle("cart-has-items", count > 0);
    latestCartSnapshot = cart;
    document.dispatchEvent(new CustomEvent("ug:cart-state"));
  };
  document.addEventListener("cart:updated", (event) => syncCartCount(event.detail?.cart));

  const updateCartCount = async () => {
    try {
      const cart = await getCart();
      syncCartCount(cart);
      return cart;
    } catch (_error) {
      return null;
    }
  };

  const addItems = async (items) => {
    const response = await fetch(`${routesRoot()}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || "This item could not be added.");
    }
    const cart = await updateCartCount();
    document.dispatchEvent(
      new CustomEvent("cart:refresh", {
        detail: { open: false },
      }),
    );
    document.dispatchEvent(
      new CustomEvent("product-ajax:added", {
        detail: { product: payload },
      }),
    );
    return { payload, cart };
  };

  const changeCartLine = async ({ key, quantity, properties }) => {
    const response = await fetch(`${routesRoot()}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ id: key, quantity, properties }),
    });
    const cart = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(cart.description || cart.message || "Your cart could not be updated.");
    }
    return cart;
  };

  const lineMatchesBundleRole = (line, item) => {
    const properties = line.properties || {};
    const wanted = item.properties || {};
    return (
      Number(line.variant_id || line.id) === Number(item.id) &&
      properties._ug_offer === wanted._ug_offer &&
      properties._ug_offer_group === wanted._ug_offer_group
    );
  };

  const addBundleWithoutDuplicates = async (items) => {
    let cart = await getCart();
    const itemsToAdd = [];
    let reclassifiedCount = 0;

    for (const item of items) {
      let quantityNeeded = Number(item.quantity || 0);
      const exactQuantity = (cart.items || [])
        .filter((line) => lineMatchesBundleRole(line, item))
        .reduce((total, line) => total + Number(line.quantity || 0), 0);
      quantityNeeded = Math.max(0, quantityNeeded - exactQuantity);

      while (quantityNeeded > 0) {
        const plainLine = (cart.items || []).find(
          (line) =>
            Number(line.variant_id || line.id) === Number(item.id) &&
            !(line.properties || {})._ug_offer,
        );
        if (!plainLine) break;
        const plainQuantity = Number(plainLine.quantity || 0);
        if (plainQuantity <= quantityNeeded) {
          cart = await changeCartLine({
            key: plainLine.key,
            quantity: plainQuantity,
            properties: item.properties,
          });
          quantityNeeded -= plainQuantity;
          reclassifiedCount += plainQuantity;
        } else {
          cart = await changeCartLine({
            key: plainLine.key,
            quantity: plainQuantity - quantityNeeded,
            properties: plainLine.properties || {},
          });
          itemsToAdd.push({ ...item, quantity: quantityNeeded });
          quantityNeeded = 0;
        }
      }

      if (quantityNeeded > 0) itemsToAdd.push({ ...item, quantity: quantityNeeded });
    }

    let result = { payload: { items: [] }, cart };
    if (itemsToAdd.length) result = await addItems(itemsToAdd);
    else await updateCartCount();
    return {
      ...result,
      addedCount: itemsToAdd.reduce((total, item) => total + Number(item.quantity || 0), 0),
      reclassifiedCount,
    };
  };

  const candidateOfferKey = (offerKind) => {
    const keys = {
      bed: "bed-builder",
      curtain: "curtain-double-up",
      wallpaper: "wallpaper-project-pack",
      rug: "rug-room-bonus",
      "bath-sheet": "bath-ritual",
      "hand-towel": "bath-ritual",
      pillow: "pillow-upgrade",
      scatter: "scatter-mix",
      "home-reset": "home-reset",
    };
    return keys[offerKind] || "";
  };

  const candidateProperties = (source) => {
    const key = candidateOfferKey(source?.dataset.ugOfferKind);
    return key ? { _ug_offer_candidate: key } : {};
  };

  const saleDisclosureLabel = "20% sale included";
  const saleDisclosureProperties = ({ selectedSale, price, compareAtPrice }) => {
    const salePrice = Number(price || 0);
    const listPrice = Number(compareAtPrice || 0);
    if (!selectedSale || !salePrice || listPrice <= salePrice) return {};
    return {
      [saleDisclosureLabel]: `${money(listPrice - salePrice)} off each (was ${money(listPrice)}).`,
    };
  };

  const cartRoleQuantity = (cart, offerKey, group) =>
    (cart?.items || []).reduce((total, line) => {
      const properties = line.properties || {};
      return properties._ug_offer === offerKey && (!group || properties._ug_offer_group === group)
        ? total + Number(line.quantity || 0)
        : total;
    }, 0);

  const cartCandidateQuantity = (cart, offerKey) =>
    (cart?.items || []).reduce((total, line) => {
      const properties = line.properties || {};
      return properties._ug_offer_candidate === offerKey || properties._ug_offer === offerKey
        ? total + Number(line.quantity || 0)
        : total;
    }, 0);

  const offerIsComplete = ({ cart, source }) => {
    const kind = source?.dataset.ugOfferKind || "generic";
    if (kind === "curtain") return cartCandidateQuantity(cart, "curtain-double-up") >= 2;
    if (kind === "wallpaper") return cartCandidateQuantity(cart, "wallpaper-project-pack") >= 4;
    if (kind === "rug") return cartRoleQuantity(cart, "rug-room-bonus", "reward") >= 2;
    if (kind === "bath-sheet" || kind === "hand-towel") {
      return (
        cartRoleQuantity(cart, "bath-ritual", "primary") >= 2 &&
        cartRoleQuantity(cart, "bath-ritual", "reward") >= 1
      );
    }
    if (kind === "bed") return cartCandidateQuantity(cart, "bed-builder") >= 3;
    if (kind === "pillow") {
      return (
        cartRoleQuantity(cart, "pillow-upgrade", "primary") >= 1 &&
        cartRoleQuantity(cart, "pillow-upgrade", "reward") >= 1
      );
    }
    if (kind === "scatter") {
      return (
        cartCandidateQuantity(cart, "scatter-mix") +
          cartCandidateQuantity(cart, "finished-scatter-multibuy") >=
        2
      );
    }
    return false;
  };

  const suggestionMatchesOffer = (card, offerKind) => {
    const tags = (card.dataset.ugSuggestionTags || "").split("|");
    if (offerKind === "scatter") {
      return tags.includes("UG:Offer:ScatterMultibuy") || tags.includes("UG:Offer:CushionCover");
    }
    if (offerKind === "bed") return tags.includes("UG:Offer:BedBuilder");
    if (offerKind === "pillow") {
      return tags.includes("UG:Offer:DuvetInner") || tags.includes("UG:Offer:PillowInner");
    }
    if (offerKind === "rug") {
      return (
        tags.includes("UG:Offer:RugBonus") ||
        tags.includes("UG:Offer:CushionCover") ||
        tags.includes("UG:Reward:RugCover")
      );
    }
    if (offerKind === "bath-sheet" || offerKind === "hand-towel") {
      return tags.includes("UG:Offer:BathSheet") || tags.includes("UG:Offer:HandTowel");
    }
    if (offerKind === "curtain") return tags.includes("UG:Offer:CurtainPair");
    if (offerKind === "wallpaper") return tags.includes("UG:Offer:Wallpaper4For3");
    if (offerKind === "home-reset") return tags.includes("UG:Offer:SpendTier");
    return false;
  };

  const modalSuggestionCards = ({ source, offerActive, recommendationMode }) => {
    if (!source) return [];
    const offerKind = source.dataset.ugOfferKind || "generic";
    const seen = new Set();
    return [...source.querySelectorAll("[data-ug-modal-source] .ug-mini-product")]
      .filter((card) => {
        if (recommendationMode === "alternate") return !suggestionMatchesOffer(card, offerKind);
        return offerActive && suggestionMatchesOffer(card, offerKind);
      })
      .filter((card) => {
        const key =
          card.dataset.ugSuggestionProduct ||
          card.querySelector("a[href]")?.getAttribute("href") ||
          card.textContent.trim();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 3);
  };

  const modal = document.querySelector("[data-ug-add-modal]");
  let modalReturnFocus = null;
  const openModal = ({
    title,
    message,
    productTitle,
    offerTitle,
    offerUrl,
    offerActive = true,
    source,
    cart,
    recommendationMode = "offer",
    recommendationsTitle,
    actionUrl,
    actionLabel,
  } = {}) => {
    if (!modal) {
      toast(message || `${productTitle || "Item"} added to your cart.`);
      return false;
    }
    const cards = modalSuggestionCards({ source, offerActive, recommendationMode });
    if (recommendationMode === "alternate" && cards.length === 0) return false;
    const showOfferPrompt = offerActive && recommendationMode === "offer";
    const heading = modal.querySelector("[data-ug-modal-title]");
    const copy = modal.querySelector("[data-ug-modal-copy]");
    const offer = modal.querySelector("[data-ug-modal-offer]");
    const offerWrap = offer?.closest(".ug-modal__offer");
    const offerLink = modal.querySelector("[data-ug-modal-bundle]");
    const cartState = modal.querySelector("[data-ug-modal-cart-state]");
    const suggestions = modal.querySelector("[data-ug-modal-suggestions]");
    const suggestionsWrap = modal.querySelector("[data-ug-modal-recommendations]");
    const suggestionsHeading = suggestionsWrap?.querySelector("h3");
    if (heading) {
      heading.textContent =
        title ||
        (recommendationMode === "alternate"
          ? "Your offer is complete. Add the next room layer?"
          : showOfferPrompt
            ? "Added to cart. Now unlock the full value."
            : "Added to cart.");
    }
    if (copy) copy.textContent = message || `${productTitle || "Your item"} is safely in your cart.`;
    if (offer) {
      if (offerWrap) offerWrap.style.display = showOfferPrompt ? "" : "none";
      offer.hidden = !showOfferPrompt;
      offer.textContent = showOfferPrompt
        ? offerTitle || "Complete the matching set and review the offer clearly in your cart."
        : "";
    }
    if (offerLink) {
      offerLink.href = actionUrl || (showOfferPrompt ? offerUrl || "/pages/offers" : "/pages/offers");
      offerLink.textContent = actionLabel || (showOfferPrompt && offerTitle ? "Complete this offer" : "See current offers");
    }
    if (cartState) {
      cartState.textContent = cart
        ? `${cart.item_count} item${cart.item_count === 1 ? "" : "s"} in cart · ${money(cart.total_price)}`
        : "";
    }
    if (suggestions && suggestionsWrap) {
      suggestions.replaceChildren();
      cards.forEach((card) => suggestions.append(card.cloneNode(true)));
      if (suggestionsHeading) {
        suggestionsHeading.textContent =
          recommendationsTitle || (recommendationMode === "alternate" ? "A smart next layer" : "Designed for your room");
      }
      suggestionsWrap.hidden = cards.length === 0;
    }
    modalReturnFocus = document.activeElement;
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    root.classList.add("overflow-hidden");
    modal.querySelector("[data-ug-modal-close]")?.focus();
    return true;
  };

  const closeModal = () => {
    if (!modal) return;
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    root.classList.remove("overflow-hidden");
    modalReturnFocus?.focus?.();
    modalReturnFocus = null;
  };

  const initModal = () => {
    if (!modal) return;
    if (modal.dataset.ugModalInit === "true") return;
    modal.dataset.ugModalInit = "true";
    modal.querySelectorAll("[data-ug-modal-close]").forEach((button) => {
      button.addEventListener("click", closeModal);
    });
    modal.querySelector("[data-ug-modal-bundle]")?.addEventListener("click", closeModal);
    modal.addEventListener("click", (event) => {
      if (event.target === modal) closeModal();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && modal.classList.contains("is-open")) {
        closeModal();
        return;
      }
      if (event.key !== "Tab" || !modal.classList.contains("is-open")) return;
      const focusable = [...modal.querySelectorAll('a[href], button:not([disabled]), select, input, textarea')].filter(
        (node) => !node.hidden && node.offsetParent !== null,
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const initHeader = () => {
    const header = document.querySelector("[data-ug-header]");
    if (header && header.dataset.ugHeaderInit !== "true") {
      header.dataset.ugHeaderInit = "true";
      const setSticky = () => header.classList.toggle("is-stuck", window.scrollY > 38);
      setSticky();
      window.addEventListener("scroll", setSticky, { passive: true });
    }

    const menu = document.querySelector("[data-ug-mobile-menu]");
    const open = document.querySelector("[data-ug-menu-open]");
    if (!menu || !open) return;
    if (menu.dataset.ugMenuInit === "true") return;
    menu.dataset.ugMenuInit = "true";
    const background = [
      document.querySelector(".ug-topbar"),
      document.querySelector("[data-ug-header]"),
      document.querySelector("main"),
      document.querySelector(".ug-footer"),
    ].filter(Boolean);

    const close = () => {
      menu.classList.remove("is-open");
      menu.setAttribute("aria-hidden", "true");
      open.setAttribute("aria-expanded", "false");
      root.classList.remove("overflow-hidden");
      background.forEach((element) => {
        element.inert = false;
      });
      open.focus();
    };
    const show = () => {
      menu.classList.add("is-open");
      menu.setAttribute("aria-hidden", "false");
      open.setAttribute("aria-expanded", "true");
      root.classList.add("overflow-hidden");
      background.forEach((element) => {
        element.inert = true;
      });
      menu.querySelector("[data-ug-menu-close]")?.focus();
    };

    open.addEventListener("click", show);
    menu.querySelectorAll("[data-ug-menu-close]").forEach((button) => button.addEventListener("click", close));
    menu.querySelectorAll("a").forEach((link) => link.addEventListener("click", close));
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && menu.classList.contains("is-open")) close();
      if (event.key !== "Tab" || !menu.classList.contains("is-open")) return;
      const focusable = [...menu.querySelectorAll('a[href], button:not([disabled]), select, input, textarea')].filter(
        (node) => !node.hidden && node.offsetParent !== null,
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const initReveal = () => {
    const nodes = [...document.querySelectorAll("[data-ug-reveal]")];
    if (!nodes.length) return;
    if (!("IntersectionObserver" in window)) {
      nodes.forEach((node) => node.classList.add("is-revealed"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-revealed");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );
    nodes.forEach((node) => observer.observe(node));
  };

  const initReviewProof = () => {
    const needsVitals = Boolean(document.querySelector("[data-ug-rating-summary]"));
    const needsGoogle = Boolean(document.querySelector("[data-ug-google-review-link]"));
    if (!needsVitals && !needsGoogle) return;

    const sync = () => {
      const vitals = document.querySelector(".vtl-pr-main-widget--product-page, .vtl-pr-main-widget");
      const google = document.querySelector(".reputon-google-reviews-widget");
      const followPendingHash = (target) => {
        if (!target || window.location.hash !== `#${target.id}` || target.dataset.ugHashScrolled === "true") return;
        target.dataset.ugHashScrolled = "true";
        window.requestAnimationFrame(() => {
          target.scrollIntoView({
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
            block: "start",
          });
        });
      };

      if (vitals && needsVitals) {
        vitals.id = "UGProductReviews";
        const scoreText = vitals.querySelector(".vtl-pr-totals__score")?.textContent?.trim() || "0";
        const countText = vitals.querySelector(".vtl-pr-totals__reviews")?.textContent?.trim() || "0 reviews";
        const score = Math.max(0, Math.min(5, Number.parseFloat(scoreText.replace(",", ".")) || 0));
        const count = Number.parseInt(countText.replace(/[^\d]/g, ""), 10) || 0;

        document.querySelectorAll("[data-ug-rating-summary]").forEach((summary) => {
          const fill = summary.querySelector("[data-ug-rating-fill]");
          const copy = summary.querySelector("[data-ug-rating-copy]");
          if (fill) fill.style.width = `${(score / 5) * 100}%`;
          if (copy) {
            const nextCopy =
              count > 0
                ? `${score.toFixed(1)} · ${count} ${count === 1 ? "review" : "reviews"}`
                : "0 reviews";
            if (copy.textContent !== nextCopy) copy.textContent = nextCopy;
          }
          summary.setAttribute(
            "aria-label",
            count > 0
              ? `Rated ${score.toFixed(1)} out of 5 from ${count} ${count === 1 ? "review" : "reviews"}`
              : "No product reviews yet. Write the first review.",
          );
          summary.hidden = count === 0;
          summary.classList.add("is-ready");
        });
        if (vitals.dataset.ugRatingObserved !== "true") {
          vitals.dataset.ugRatingObserved = "true";
          const ratingObserver = new MutationObserver(sync);
          ratingObserver.observe(vitals, { childList: true, subtree: true, characterData: true });
        }
        followPendingHash(vitals);
      }

      if (google && needsGoogle) {
        google.id = "UGGoogleReviews";
        document.querySelectorAll("[data-ug-google-review-link]").forEach((link) => {
          link.setAttribute("aria-label", "Read genuine customer reviews on Google");
          link.hidden = false;
          link.classList.add("is-ready");
        });
        followPendingHash(google);
      }

      return (!needsVitals || Boolean(vitals)) && (!needsGoogle || Boolean(google));
    };

    if (sync()) return;
    const observer = new MutationObserver(() => {
      if (sync()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => {
      sync();
      observer.disconnect();
    }, 12000);
  };

  const initTilt = () => {
    if (!window.matchMedia("(pointer: fine)").matches || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    document.querySelectorAll("[data-ug-tilt]").forEach((card) => {
      card.addEventListener("pointermove", (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        card.style.transform = `perspective(900px) rotateX(${y * -4}deg) rotateY(${x * 5}deg) translateY(-6px)`;
      });
      card.addEventListener("pointerleave", () => {
        card.style.removeProperty("transform");
      });
    });
  };

  const initQuickAdd = () => {
    if (root.dataset.ugQuickAddInit === "true") return;
    root.dataset.ugQuickAddInit = "true";
    document.addEventListener("change", (event) => {
      const select = event.target.closest("[data-ug-inline-variant]");
      if (!select) return;
      const card = select.closest(".ug-mini-product, [data-ug-compatible-card]");
      const button = card?.querySelector("[data-ug-quick-add]");
      const price = card?.querySelector("[data-ug-inline-price]");
      const option = select.selectedOptions?.[0];
      if (button) {
        button.dataset.ugQuickAdd = select.value;
        button.dataset.ugQuickPrice = option?.dataset.ugPrice || "0";
        button.dataset.ugQuickCompare = option?.dataset.ugCompare || "0";
        if (select.matches("[data-ug-compatible-select]")) {
          button.dataset.ugCompatibleValid = String(Boolean(select.value) && option?.dataset.ugAvailable !== "false");
        }
        if (button.dataset.ugBusy !== "true") {
          button.disabled = !select.value || button.dataset.ugCompatibleValid === "false";
        }
      }
      const status = card?.querySelector("[data-ug-quick-status]");
      if (status && select.value) {
        status.textContent = "";
        status.classList.remove("is-error");
      }
      if (price && option?.dataset.ugPrice) price.textContent = money(option.dataset.ugPrice);
      const compare = card?.querySelector("[data-ug-inline-compare]");
      const saving = card?.querySelector("[data-ug-inline-saving]");
      const selectedPrice = Number(option?.dataset.ugPrice || 0);
      const comparedPrice = Number(option?.dataset.ugCompare || 0);
      if (compare) {
        compare.hidden = !select.value || comparedPrice <= selectedPrice;
        compare.textContent = comparedPrice > selectedPrice ? money(comparedPrice) : "";
      }
      if (saving) saving.hidden = !select.value || card.dataset.ugSelectedSale !== "true" || comparedPrice <= selectedPrice;
    });
    document.addEventListener("click", async (event) => {
      const button = event.target.closest("[data-ug-quick-add]");
      if (!button) return;
      event.preventDefault();
      const variantId = Number(button.dataset.ugQuickAdd);
      if (!variantId || button.disabled) return;
      const status = button.closest("[data-ug-room-card], [data-ug-compatible-card]")?.querySelector("[data-ug-quick-status]");
      const original = button.textContent;
      const offerActive = button.dataset.ugOfferActive !== "false";
      const source = button.closest("[data-ug-product]");
      const saleContext = button.closest("[data-ug-selected-sale]");
      const lineProperties = {
        ...(offerActive ? candidateProperties(source) : {}),
        ...saleDisclosureProperties({
          selectedSale: saleContext?.dataset.ugSelectedSale === "true",
          price: button.dataset.ugQuickPrice,
          compareAtPrice: button.dataset.ugQuickCompare,
        }),
      };
      button.disabled = true;
      button.dataset.ugBusy = "true";
      button.textContent = "Adding…";
      if (status) { status.textContent = "Adding to your cart…"; status.classList.remove("is-error"); }
      try {
        const { cart } = await addItems([
          {
            id: variantId,
            quantity: 1,
            properties: lineProperties,
          },
        ]);
        if (status) status.textContent = "Added to your cart.";
        if (document.querySelector("body.cart-template, main-cart, form#cart, [data-ug-cart-form], [data-ug-cart-page]")) {
          window.location.reload();
          return;
        }
        if (offerActive && offerIsComplete({ cart, source })) {
          const openedAlternate = openModal({
            title: "Your offer is complete. Add the next room layer?",
            message: `${button.dataset.ugProductTitle || "Your item"} is in your cart and this promotion is already complete. These complementary choices sit outside the same offer.`,
            productTitle: button.dataset.ugProductTitle,
            offerActive,
            source,
            cart,
            recommendationMode: "alternate",
            recommendationsTitle: "Chosen to complement your room",
            actionUrl: `${routesRoot()}pages/offers`,
            actionLabel: "Explore other offers",
          });
          if (!openedAlternate) window.location.assign(`${routesRoot()}cart`);
          return;
        }
        openModal({
          productTitle: button.dataset.ugProductTitle,
          message: `${button.dataset.ugProductTitle || "Your item"} is in your cart.`,
          offerTitle: button.dataset.ugOfferTitle,
          offerUrl: button.dataset.ugOfferUrl,
          offerActive,
          source,
          cart,
        });
      } catch (error) {
        const message = error.message || "This item could not be added. Please choose the option again.";
        if (status) { status.textContent = message; status.classList.add("is-error"); }
        toast(message, true);
      } finally {
        delete button.dataset.ugBusy;
        button.disabled = button.dataset.ugCompatibleValid === "false";
        button.textContent = original;
      }
    });
  };

  const initProductForms = () => {
    document.querySelectorAll("[data-ug-product]").forEach((scope) => {
      const form = scope.querySelector(".ug-product-form");
      const json = scope.querySelector("[data-ug-variants]");
      if (!form || !json || form.dataset.ugProductFormInit === "true") return;
      let variants = [];
      let stockByVariant = {};
      try {
        variants = JSON.parse(json.textContent);
      } catch (_error) {
        return;
      }
      const stockJson = scope.querySelector("[data-ug-variant-stock]");
      if (stockJson) {
        try {
          stockByVariant = JSON.parse(stockJson.textContent);
        } catch (_error) {
          stockByVariant = {};
        }
      }

      form.dataset.ugProductFormInit = "true";
      const selects = [...form.querySelectorAll("[data-ug-option]")];
      const idInput = form.querySelector("[data-ug-variant-id]");
      const add = form.querySelector("[data-ug-add]");
      const stickyAdd = document.querySelector(`[data-ug-sticky-add="${scope.dataset.ugProduct}"]`);
      const price = scope.querySelector("[data-ug-price]");
      const compare = scope.querySelector("[data-ug-compare]");
      const status = form.querySelector("[data-ug-form-status]");
      const stock = scope.querySelector("[data-ug-stock]");
      const stickyPrice = stickyAdd?.closest("[data-ug-sticky]")?.querySelector("[data-ug-sticky-price]");
      const saving = scope.querySelector("[data-ug-saving]");
      const salePanel = scope.querySelector("[data-ug-sale-panel]");
      const selectionSummary = scope.querySelector("[data-ug-selection]");
      const quantityInput = form.querySelector("[name='quantity']");
      const packs = form.querySelector("[data-ug-quantity-packs]");
      let quantityConfig = {};
      try { quantityConfig = JSON.parse(form.querySelector("[data-ug-quantity-config]")?.textContent || "{}"); } catch (_) { /* Keep native quantity available. */ }
      const packOfferActive = () => quantityConfig.enabled === true &&
        (window.Shopify?.currency?.active || "ZAR") === quantityConfig.currency &&
        Date.now() < Date.parse(quantityConfig.endsAt);
      const packPrice = (variant, quantity) => {
        const base = Number(variant?.price || 0) * quantity;
        const eligible = packOfferActive() && quantityConfig.variantIds?.includes(String(variant?.id));
        const discount = eligible ? (quantityConfig.tiers || []).reduce((best, tier) =>
          quantity >= Number(tier.quantity) ? Math.max(best, Number(tier.amount) || 0) : best, 0) : 0;
        return { base, discount: Math.min(base, discount), total: Math.max(0, base - discount), eligible };
      };
      const quantityState = (variant) => {
        const count = Number(quantityInput?.value || 0);
        const inventory = stockByVariant[String(variant?.id)] || {};
        const max = inventory.tracked === true && inventory.continues !== true
          ? Math.max(0, Number(inventory.quantity) || 0) : Infinity;
        return { count, max, valid: Number.isInteger(count) && count >= 1 && count <= max };
      };
      const renderPacks = (variant) => {
        const state = quantityState(variant);
        if (quantityInput) {
          if (Number.isFinite(state.max)) quantityInput.max = String(state.max);
          else quantityInput.removeAttribute("max");
          quantityInput.setAttribute("aria-invalid", String(!state.valid));
        }
        if (!packs) return state;
        const current = packPrice(variant, state.count);
        packs.hidden = !variant?.available || !current.eligible;
        if (packs.hidden) return state;
        const existingQuantity = latestCartSnapshot?.items
          ? latestCartSnapshot.items.reduce((count, line) => quantityConfig.variantIds?.includes(String(line.variant_id || line.id)) ? count + Number(line.quantity || 0) : count, 0)
          : Number(quantityConfig.cartEligibleQuantity || 0);
        packs.querySelectorAll("[data-ug-pack-quantity]").forEach((button) => {
          const count = Number(button.dataset.ugPackQuantity);
          const offer = packPrice(variant, count);
          button.disabled = productSubmitting || count > state.max;
          button.setAttribute("aria-pressed", String(state.count === count));
          const shownTotal = existingQuantity > 0 ? offer.base : offer.total;
          button.querySelector("[data-ug-pack-total]").textContent = `${money(shownTotal)} ${existingQuantity > 0 ? "before cart offer" : "total"}`;
          const unitCents = shownTotal / count;
          button.querySelector("[data-ug-pack-unit]").textContent = `${Number.isInteger(unitCents) ? "" : "≈ "}${money(unitCents)} per cover`;
          button.querySelector("[data-ug-pack-saving]").textContent = existingQuantity > 0
            ? "Offer calculated in cart" : offer.discount > 0 ? `Extra ${money(offer.discount)} off` : "Current item price";
        });
        const summary = packs.querySelector("[data-ug-pack-selection]");
        if (summary) summary.textContent = !state.valid ? "Choose an available whole-number quantity."
          : existingQuantity > 0
            ? `${existingQuantity} eligible ${existingQuantity === 1 ? "cover is" : "covers are"} already in your cart. ${state.count === 1 ? "This cover totals" : `These ${state.count} covers total`} ${money(current.base)} before cart discounts. Your cart applies the highest eligible quantity offer once across all ${existingQuantity + state.count} covers.`
            : `${state.count} ${state.count === 1 ? "cover" : "covers"} · ${money(current.total)} for this selection${current.discount > 0 ? ` · extra ${money(current.discount)} saving` : ""}`;
        return state;
      };
      let purchaseLabels = {};
      try { purchaseLabels = JSON.parse(scope.querySelector("[data-ug-purchase-labels]")?.textContent || "{}"); } catch (_) { /* Keep native form available. */ }
      let productSubmitting = false;
      if (idInput) idInput.name = "id";

      const selected = () => selects.map((select) => select.value);
      const findVariant = () => {
        if (selects.length === 0) {
          return (
            variants.find((variant) => String(variant.id) === String(idInput?.value)) ||
            variants.find((variant) => variant.available) ||
            variants[0]
          );
        }
        const values = selected();
        return variants.find((variant) => {
          const options = variant.options || variant.selectedOptions?.map((option) => option.value) || [];
          return options.every((value, index) => value === values[index]);
        });
      };
      const render = (updateMedia = false) => {
        const variant = findVariant();
        if (!variant) {
          renderPacks(null);
          if (idInput) idInput.value = "";
          if (add) {
            add.disabled = true;
            add.textContent = "Unavailable combination";
          }
          if (stickyAdd) {
            stickyAdd.disabled = true;
            delete stickyAdd.dataset.ugVariant;
            stickyAdd.textContent = "Unavailable combination";
          }
          if (price) price.textContent = "—";
          if (stickyPrice) stickyPrice.textContent = "—";
          if (compare) compare.hidden = true;
          if (stock) stock.hidden = true;
          if (saving) saving.hidden = true;
          if (salePanel) salePanel.hidden = true;
          if (selectionSummary) selectionSummary.textContent = purchaseLabels.unavailable || "Unavailable combination";
          if (status) {
            status.className = "ug-product-form__status is-error";
            status.textContent = "This selected variant is currently unavailable.";
          }
          return;
        }
        if (idInput) idInput.value = variant.id;
        const quantity = renderPacks(variant);
        scope
          .querySelectorAll(
            `[data-ug-bundle-card][data-ug-product-id="${scope.dataset.ugProduct}"] [data-ug-bundle-variant]`,
          )
          .forEach((bundleControl) => {
            const canSelectVariant =
              bundleControl.tagName !== "SELECT" ||
              [...bundleControl.options].some((option) => String(option.value) === String(variant.id));
            if (!canSelectVariant) return;
            bundleControl.value = variant.id;
            const card = bundleControl.closest("[data-ug-bundle-card]");
            const bundlePrice = card?.querySelector("[data-ug-bundle-price]");
            const quantity = Number(card?.querySelector("[data-ug-bundle-quantity]")?.value || card?.dataset.ugQuantity || 1);
            if (bundlePrice) bundlePrice.textContent = `${money(variant.price)}${quantity > 1 ? ` × ${quantity}` : ""}`;
            const bundleForm = card?.closest("[data-ug-bundle-form]");
            if (bundleForm) updateBundleBar(bundleForm);
          });
        if (selectionSummary) {
          selectionSummary.textContent = scope.dataset.ugProductSingle === "true"
            ? scope.dataset.ugProductTitle || ""
            : variant.public_title || variant.title || "";
        }
        const isSelectedSale = scope.dataset.ugSelectedSale === "true" && Number(variant.compare_at_price) > Number(variant.price);
        if (saving) {
          saving.hidden = !isSelectedSale;
          saving.textContent = isSelectedSale ? `${purchaseLabels.save || "Save"} ${money(Number(variant.compare_at_price) - Number(variant.price))} · ${purchaseLabels.applied || "Saving already included"}` : "";
        }
        if (salePanel) salePanel.hidden = !isSelectedSale;
        if (price) price.textContent = money(variant.price);
        if (stickyPrice) {
          const pack = packPrice(variant, quantity.count);
          const existingEligible = latestCartSnapshot?.items
            ? latestCartSnapshot.items.some((line) => quantityConfig.variantIds?.includes(String(line.variant_id || line.id)))
            : Number(quantityConfig.cartEligibleQuantity || 0) > 0;
          stickyPrice.textContent = quantity.valid && pack.eligible && quantity.count > 1
            ? `${quantity.count} covers · ${money(existingEligible ? pack.base : pack.total)}${existingEligible ? " before cart offer" : ""}` : money(variant.price);
        }
        if (compare) {
          const show = Number(variant.compare_at_price) > Number(variant.price);
          compare.hidden = !show;
          compare.textContent = show ? money(variant.compare_at_price) : "";
        }
        if (updateMedia && variant.featured_media?.id) {
          scope.querySelectorAll("[data-ug-gallery-item]").forEach((item) => {
            item.classList.toggle("is-variant-selected", item.dataset.ugMediaId === String(variant.featured_media.id));
          });
        }
        if (add) {
          add.disabled = productSubmitting || !variant.available || !quantity.valid;
          add.textContent = productSubmitting ? "Adding…" : !variant.available ? "Sold out" : quantity.valid && quantity.count > 1 ? `Add ${quantity.count} to cart` : "Add to cart";
        }
        if (stickyAdd) {
          stickyAdd.disabled = productSubmitting || !variant.available || !quantity.valid;
          stickyAdd.dataset.ugVariant = variant.id;
          stickyAdd.textContent = productSubmitting ? "Adding…" : !variant.available ? "Sold out" : quantity.valid && quantity.count > 1 ? `Add ${quantity.count} to cart` : "Add to cart";
        }
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
        if (status) {
          status.className = "ug-product-form__status";
          status.textContent = !variant.available ? "This selected variant is currently unavailable."
            : !quantity.valid ? Number.isFinite(quantity.max) && quantity.count > quantity.max
              ? `Only ${quantity.max} are available in this option. Choose a lower quantity.`
              : "Choose a whole-number quantity of at least 1." : "";
          if (!quantity.valid) status.classList.add("is-error");
        }
        if (stock) {
          const variantStock = stockByVariant[String(variant.id)] || {};
          const tracked = variantStock.tracked === true;
          const quantity = Number(variantStock.quantity);
          const continues = variantStock.continues === true;
          const threshold = Math.max(1, Number(stock.dataset.ugStockThreshold || 9));
          const isLow =
            tracked &&
            !continues &&
            variant.available &&
            variantStock.available === true &&
            Number.isFinite(quantity) &&
            quantity > 0 &&
            quantity <= threshold;
          const stockMessage = stock.querySelector("[data-ug-stock-message]");
          const stockProgress = stock.querySelector("[data-ug-stock-progress]");
          const stockDate = stock.querySelector("[data-ug-stock-date]");
          stock.hidden = !isLow;
          if (isLow) {
            const level = Math.max(8, Math.min(100, (quantity / threshold) * 100));
            stock.style.setProperty("--ug-stock-level", `${level}%`);
            if (stockMessage) stockMessage.textContent = `Only ${quantity} left in this option.`;
            if (stockProgress) {
              stockProgress.setAttribute("aria-valuenow", String(quantity));
              stockProgress.setAttribute("aria-valuemax", String(threshold));
              stockProgress.setAttribute("aria-label", `${quantity} of ${threshold} low-stock units remain`);
            }
            if (stockDate) {
              const now = new Date();
              const dateLabel = new Intl.DateTimeFormat("en-ZA", {
                day: "numeric",
                month: "long",
                year: "numeric",
                timeZone: "Africa/Johannesburg",
              }).format(now);
              const dateValue = new Intl.DateTimeFormat("en-CA", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                timeZone: "Africa/Johannesburg",
              }).format(now);
              stockDate.textContent = dateLabel;
              stockDate.setAttribute("datetime", dateValue);
            }
          }
        }
        scope.dispatchEvent(new CustomEvent("ug:variant-change", { detail: { variant } }));
      };
      selects.forEach((select) => select.addEventListener("change", () => render(true)));
      quantityInput?.addEventListener("input", () => render());
      document.addEventListener("ug:cart-state", () => { if (form.isConnected) render(); });
      packs?.addEventListener("click", (event) => {
        const button = event.target.closest("[data-ug-pack-quantity]");
        if (!button || button.disabled || !quantityInput) return;
        quantityInput.value = button.dataset.ugPackQuantity;
        render();
      });
      render();

      const submit = async (event) => {
        event.preventDefault();
        if (productSubmitting) return;
        const variant = findVariant();
        if (!variant?.available || !quantityState(variant).valid) { render(); return; }
        const quantity = quantityState(variant).count;
        productSubmitting = true;
        render();
        if (status) {
          status.className = "ug-product-form__status";
          status.textContent = "Adding your selection…";
        }
        let outcomeMessage = "";
        let outcomeError = false;
        try {
          const offerActive = scope.dataset.ugOfferActive !== "false";
          const lineProperties = {
            ...(offerActive ? candidateProperties(scope) : {}),
            ...saleDisclosureProperties({
              selectedSale: scope.dataset.ugSelectedSale === "true",
              price: variant.price,
              compareAtPrice: variant.compare_at_price,
            }),
          };
          const { cart } = await addItems([
            {
              id: variant.id,
              quantity,
              properties: lineProperties,
            },
          ]);
          outcomeMessage = "Added to cart.";
          if (status) {
            status.className = "ug-product-form__status is-success";
            status.textContent = outcomeMessage;
          }
          if (offerActive && offerIsComplete({ cart, source: scope })) {
            const openedAlternate = openModal({
              title: "Your offer is complete. Add the next room layer?",
              message: `${scope.dataset.ugProductTitle || "Your selection"} is in your cart and this promotion is already complete. These complementary choices sit outside the same offer.`,
              productTitle: scope.dataset.ugProductTitle,
              offerActive,
              source: scope,
              cart,
              recommendationMode: "alternate",
              recommendationsTitle: "Chosen to complement your room",
              actionUrl: `${routesRoot()}pages/offers`,
              actionLabel: "Explore other offers",
            });
            if (!openedAlternate) window.location.assign(`${routesRoot()}cart`);
            return;
          }
          openModal({
            productTitle: scope.dataset.ugProductTitle,
            message: `${scope.dataset.ugProductTitle || "Your selection"} is in your cart.`,
            offerTitle: scope.dataset.ugOfferTitle,
            offerUrl: scope.dataset.ugOfferUrl,
            offerActive,
            source: scope,
            cart,
          });
        } catch (error) {
          outcomeMessage = error.message || "Your selection could not be added. Please try again.";
          outcomeError = true;
          toast(outcomeMessage, true);
        } finally {
          productSubmitting = false;
          render();
          if (status && outcomeMessage) {
            status.className = `ug-product-form__status ${outcomeError ? "is-error" : "is-success"}`;
            status.textContent = outcomeMessage;
          }
        }
      };
      form.addEventListener("submit", submit);

      if (stickyAdd) {
        stickyAdd.addEventListener("click", () => {
          if (!productSubmitting && !stickyAdd.disabled) form.requestSubmit();
        });
      }

      const sticky = stickyAdd?.closest("[data-ug-sticky]");
      if (sticky && "IntersectionObserver" in window) {
        let formIsPast = false;
        let builderIsVisible = false;
        const updateSticky = () => sticky.classList.toggle("is-visible", formIsPast && !builderIsVisible);
        const observer = new IntersectionObserver(([entry]) => {
          formIsPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          updateSticky();
        });
        observer.observe(form);
        const builder = document.getElementById("UGProductOffer");
        if (builder) {
          const builderObserver = new IntersectionObserver(([entry]) => {
            builderIsVisible = entry.isIntersecting;
            updateSticky();
          });
          builderObserver.observe(builder);
        }
      }
    });
  };

  const parseRequirements = (form) => {
    try {
      return JSON.parse(form.dataset.ugRequirements || "{}");
    } catch (_error) {
      return {};
    }
  };

  const parseMaxCards = (form) => {
    try {
      return JSON.parse(form.dataset.ugMaxCards || "{}");
    } catch (_error) {
      return {};
    }
  };

  const parseExactQuantities = (form) => {
    try {
      return JSON.parse(form.dataset.ugExactQuantities || "{}");
    } catch (_error) {
      return {};
    }
  };

  const parseAllowedQuantities = (form) => {
    try {
      return JSON.parse(form.dataset.ugAllowedQuantities || "{}");
    } catch (_error) {
      return {};
    }
  };

  const invalidAllowedQuantity = (form, groups) =>
    Object.entries(parseAllowedQuantities(form)).find(([group, values]) => {
      const allowed = Array.isArray(values) ? values.map(Number) : [];
      return allowed.length && !allowed.includes(groups[group] || 0);
    });

  const checkedCardsForGroup = (form, group) =>
    [...form.querySelectorAll("[data-ug-bundle-card]")].filter(
      (card) =>
        (card.dataset.ugGroup || "items") === group &&
        card.querySelector("[data-ug-bundle-check]")?.checked,
    );

  const selectedOptionValues = (form, optionTerms, cardFilter = () => true) =>
    [...form.querySelectorAll("[data-ug-bundle-card]")]
      .filter((card) => card.querySelector("[data-ug-bundle-check]")?.checked && cardFilter(card))
      .map((card) => {
        const control = card.querySelector("[data-ug-bundle-variant]");
        const option = control?.selectedOptions?.[0];
        const optionData = option?.dataset || control?.dataset;
        if (!control || !optionData) return "";
        try {
          const values = JSON.parse(optionData.ugVariantOptions || "[]");
          const names = JSON.parse(optionData.ugOptionNames || "[]");
          const optionIndex = names.findIndex((name) =>
            optionTerms.some((term) => String(name).toLowerCase().includes(term)),
          );
          if (optionIndex >= 0) return String(values[optionIndex] || "").trim().toLowerCase();
          if (optionTerms.some((term) => ["size", "drop", "dimension"].includes(term))) {
            return String(card.dataset.ugCompatibilitySize || "").trim().toLowerCase();
          }
          return "";
        } catch (_error) {
          return "";
        }
      })
      .filter(Boolean);

  const selectedSizeValues = (form) => {
    const sizes = selectedOptionValues(
      form,
      ["size", "drop", "dimension"],
      (card) =>
        form.dataset.ugSizeMode !== "bed" ||
        !/pillowcase|pillow inner|pillow protector/i.test(card.dataset.ugProductType || ""),
    ).map((size) => size.replace(/\s+/g, " ").trim());
    if (form.dataset.ugSizeMode === "bed") {
      const bedSizes = new Set([
        "single",
        "single xl",
        "three quarter",
        "three quarter xl",
        "double",
        "double xl",
        "queen",
        "queen xl",
        "king",
        "king xl",
        "super king",
        "super king xl",
      ]);
      return sizes.filter((size) => bedSizes.has(size));
    }
    return sizes
      .map((size) => {
        const dimension = size.match(/(\d{2,3})\s*[x×]\s*(\d{2,3})/i);
        return dimension ? `${dimension[1]}x${dimension[2]}` : size;
      })
      .filter(Boolean);
  };

  const selectedColorValues = (form) => selectedOptionValues(form, ["color", "colour"]);

  const selectedGsmValues = (form) => selectedOptionValues(form, ["gsm"]);

  const bundleCompatibilityError = (form) => {
    if (form.dataset.ugMatchSize === "true") {
      const sizes = selectedSizeValues(form);
      if (sizes.length > 1 && new Set(sizes).size > 1) {
        return "Choose one matching size across every size-based item in this builder.";
      }
    }
    if (form.dataset.ugMatchColor === "true") {
      const colors = selectedColorValues(form);
      if (colors.length > 1 && new Set(colors).size > 1) {
        return "Choose the same colour across every item in this set.";
      }
    }
    if (form.dataset.ugMatchGsm === "true") {
      const weights = selectedGsmValues(form);
      if (weights.length > 1 && new Set(weights).size > 1) {
        return "Choose the same towel weight (GSM) across every item in this set.";
      }
    }
    return "";
  };

  const bundleSelection = (form) => {
    const cards = [...form.querySelectorAll("[data-ug-bundle-card]")];
    return cards
      .filter((card) => card.querySelector("[data-ug-bundle-check]")?.checked)
      .map((card) => ({
        id: Number(card.querySelector("[data-ug-bundle-variant]")?.value),
        quantity: Math.max(
          1,
          Number(card.querySelector("[data-ug-bundle-quantity]")?.value || card.dataset.ugQuantity || 1),
        ),
        group: card.dataset.ugGroup || "items",
      }))
      .filter((item) => item.id);
  };

  const bundleGroupLabel = (group, count = 2) => {
    const labels = {
      primary: ["main offer item", "main offer items"],
      reward: ["free add-on", "free add-ons"],
      cover: ["cushion cover", "cushion covers"],
      inner: ["matching inner", "matching inners"],
      scatter: ["eligible scatter or cover", "eligible scatters or covers"],
      item: ["eligible item", "eligible items"],
    };
    const pair = labels[group];
    if (pair) return pair[Number(count) === 1 ? 0 : 1];
    return `${group} item${Number(count) === 1 ? "" : "s"}`;
  };

  const updateBundleBar = (form, { preserveStatus = false } = {}) => {
    const items = bundleSelection(form);
    const count = items.reduce((sum, item) => sum + item.quantity, 0);
    const groups = items.reduce((acc, item) => {
      acc[item.group] = (acc[item.group] || 0) + item.quantity;
      return acc;
    }, {});
    const requirements = parseRequirements(form);
    const exactQuantities = parseExactQuantities(form);
    const hasMinimums = Object.entries(requirements).every(
      ([group, minimum]) => (groups[group] || 0) >= Number(minimum),
    );
    const hasExactQuantities = Object.entries(exactQuantities).every(
      ([group, exact]) => (groups[group] || 0) === Number(exact),
    );
    const invalidQuantity = invalidAllowedQuantity(form, groups);
    const compatibilityError = bundleCompatibilityError(form);
    const isReady = hasMinimums && hasExactQuantities && !invalidQuantity && !compatibilityError;
    const countNode = form.querySelector("[data-ug-bundle-count]");
    if (countNode) countNode.textContent = `${count} item${count === 1 ? "" : "s"} selected`;
    const button = form.querySelector("[data-ug-bundle-submit]");
    if (button && !button.dataset.ugBusy) button.disabled = !isReady;
    const status = form.querySelector("[data-ug-bundle-status]");
    if (status && !preserveStatus) {
      if (compatibilityError) {
        status.textContent = compatibilityError;
      } else if (invalidQuantity) {
        const allowed = parseAllowedQuantities(form)[invalidQuantity[0]];
        const labelCount = allowed.length === 1 ? Number(allowed[0]) : 2;
        status.textContent = `Choose exactly ${allowed.join(" or ")} ${bundleGroupLabel(invalidQuantity[0], labelCount)}.`;
      } else if (isReady) {
        status.textContent = "Ready to add. Confirm the selected options, then add the complete set.";
      }
    }
  };

  const initBundleForms = () => {
    document.querySelectorAll("[data-ug-bundle-form]").forEach((form) => {
      if (window.matchMedia("(max-width: 820px)").matches) {
        const mobileBar = form.querySelector(":scope > .ug-bundle__bar");
        const bundleHead = form.querySelector(":scope > .ug-bundle__head");
        if (mobileBar && bundleHead && !mobileBar.classList.contains("ug-bundle__bar--mobile-top")) {
          mobileBar.classList.add("ug-bundle__bar--mobile-top");
          bundleHead.after(mobileBar);
        }
      }
      form.querySelectorAll(".ug-bundle__products").forEach((grid) => {
        const cards = grid.querySelectorAll("[data-ug-bundle-card]");
        const collapsedCardLimit = window.matchMedia("(max-width: 560px)").matches
          ? 4
          : window.matchMedia("(max-width: 820px)").matches
            ? 6
            : 8;
        if (cards.length <= collapsedCardLimit || grid.nextElementSibling?.matches("[data-ug-bundle-toggle]")) return;
        grid.classList.add("is-collapsed");
        const toggle = document.createElement("button");
        toggle.type = "button";
        toggle.className = "ug-button ug-button--outline ug-bundle__toggle";
        toggle.dataset.ugBundleToggle = "";
        toggle.setAttribute("aria-expanded", "false");
        toggle.textContent = `Show all ${cards.length} choices`;
        toggle.addEventListener("click", () => {
          const isExpanded = toggle.getAttribute("aria-expanded") === "true";
          toggle.setAttribute("aria-expanded", String(!isExpanded));
          grid.classList.toggle("is-collapsed", isExpanded);
          toggle.textContent = isExpanded ? `Show all ${cards.length} choices` : "Show fewer choices";
        });
        grid.after(toggle);
      });
      let bundleSubmitting = false;
      form.addEventListener("change", (event) => {
        const checkbox = event.target.closest("[data-ug-bundle-check]");
        const changedCard = checkbox?.closest("[data-ug-bundle-card]");
        if (changedCard) changedCard.classList.toggle("is-selected", checkbox.checked);
        if (checkbox?.checked) {
          const card = checkbox.closest("[data-ug-bundle-card]");
          const group = card?.dataset.ugGroup || "items";
          const maximum = Number(parseMaxCards(form)[group] || 0);
          if (maximum && checkedCardsForGroup(form, group).length > maximum) {
            checkbox.checked = false;
            changedCard?.classList.remove("is-selected");
            const message = `Choose no more than ${maximum} ${group} card${maximum === 1 ? "" : "s"} for this builder.`;
            form.querySelector("[data-ug-bundle-status]").textContent = message;
            toast(message, true);
          }
        }
        updateBundleBar(form);
      });
      form.addEventListener("input", (event) => {
        if (!event.target.closest("[data-ug-bundle-quantity]")) return;
        updateBundleBar(form);
      });
      const submitBundle = async (event) => {
        event?.preventDefault();
        if (bundleSubmitting) return;
        const requirements = parseRequirements(form);
        const exactQuantities = parseExactQuantities(form);
        const items = bundleSelection(form);
        const groups = items.reduce((acc, item) => {
          acc[item.group] = (acc[item.group] || 0) + item.quantity;
          return acc;
        }, {});
        const missing = Object.entries(requirements).find(([group, minimum]) => (groups[group] || 0) < Number(minimum));
        const status = form.querySelector("[data-ug-bundle-status]");
        if (missing) {
          const message = `Choose at least ${missing[1]} ${bundleGroupLabel(missing[0], missing[1])}.`;
          if (status) status.textContent = message;
          toast(message, true);
          return;
        }
        const incorrectQuantity = Object.entries(exactQuantities).find(
          ([group, exact]) => (groups[group] || 0) !== Number(exact),
        );
        if (incorrectQuantity) {
          const message = `Choose exactly ${incorrectQuantity[1]} ${incorrectQuantity[0]} item${Number(incorrectQuantity[1]) === 1 ? "" : "s"} for this offer.`;
          if (status) status.textContent = message;
          toast(message, true);
          return;
        }
        const invalidQuantity = invalidAllowedQuantity(form, groups);
        if (invalidQuantity) {
          const allowed = parseAllowedQuantities(form)[invalidQuantity[0]];
          const labelCount = allowed.length === 1 ? Number(allowed[0]) : 2;
          const message = `Choose exactly ${allowed.join(" or ")} ${bundleGroupLabel(invalidQuantity[0], labelCount)} for this offer.`;
          if (status) status.textContent = message;
          toast(message, true);
          return;
        }
        const compatibilityError = bundleCompatibilityError(form);
        if (compatibilityError) {
          if (status) status.textContent = compatibilityError;
          toast(compatibilityError, true);
          return;
        }
        const button = form.querySelector("[data-ug-bundle-submit]");
        const original = button?.textContent;
        bundleSubmitting = true;
        if (button) {
          button.dataset.ugBusy = "true";
          button.disabled = true;
          button.textContent = "Adding bundle…";
        }
        if (status) status.textContent = "Adding your selected bundle…";
        let submitFailed = false;
        try {
          const offerKey = form.dataset.ugOfferKey || "guided-bundle";
          const bundleId = `ug-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
          const cartItems = items.map(({ id, quantity, group }) => ({
            id,
            quantity,
            properties: {
              _ug_offer: offerKey,
              _ug_offer_group: group,
              _ug_bundle_id: bundleId,
              _ug_offer_elected: "true",
            },
          }));
          const { addedCount, reclassifiedCount } = await addBundleWithoutDuplicates(cartItems);
          const updatedCount = addedCount + reclassifiedCount;
          if (status) {
            status.textContent = updatedCount
              ? `Your complete set is ready. ${updatedCount} item${updatedCount === 1 ? "" : "s"} added or matched to this offer without duplicating your existing selection.`
              : "Your complete set was already in the cart. Nothing was duplicated.";
          }
          if (document.querySelector("[data-ug-cart-form]")) {
            window.location.reload();
            return;
          }
          window.location.assign(`${routesRoot()}cart`);
          return;
        } catch (error) {
          submitFailed = true;
          if (status) status.textContent = error.message;
          toast(error.message, true);
        } finally {
          if (button) {
            delete button.dataset.ugBusy;
            button.disabled = false;
            button.textContent = original;
          }
          bundleSubmitting = false;
          updateBundleBar(form, { preserveStatus: submitFailed });
        }
      };
      form.addEventListener("submit", submitBundle);
      form.querySelector("[data-ug-bundle-submit]")?.addEventListener("click", submitBundle);
      updateBundleBar(form);
    });
  };

  const initCartForm = () => {
    const form = document.querySelector("[data-ug-cart-form]");
    if (!form) return;
    let timer;
    form.querySelectorAll("[data-ug-cart-quantity]").forEach((input) => {
      input.addEventListener("change", () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => form.submit(), 350);
      });
    });
  };

  const initCartSaleDisclosures = async () => {
    if (root.dataset.ugSaleDisclosureInit === "true") return;
    const nodes = [...document.querySelectorAll("[data-ug-cart-sale-disclosure]")];
    if (!nodes.length) return;
    root.dataset.ugSaleDisclosureInit = "true";
    const lines = new Map();
    nodes.forEach((node) => {
      const key = node.dataset.ugCartLineKey;
      if (!key || lines.has(key)) return;
      lines.set(key, {
        key,
        variantId: Number(node.dataset.ugVariantId || 0),
        price: Number(node.dataset.ugSalePrice || 0),
        compareAtPrice: Number(node.dataset.ugSaleListPrice || 0),
      });
    });
    try {
      let cart = await getCart();
      let changed = false;
      for (const metadata of lines.values()) {
        const wanted = saleDisclosureProperties({ selectedSale: true, ...metadata })[saleDisclosureLabel];
        if (!wanted) continue;
        const line = (cart.items || []).find((item) => item.key === metadata.key) ||
          (cart.items || []).find(
            (item) =>
              Number(item.variant_id || item.id) === metadata.variantId &&
              (item.properties || {})[saleDisclosureLabel] !== wanted,
          );
        if (!line || (line.properties || {})[saleDisclosureLabel] === wanted) continue;
        cart = await changeCartLine({
          key: line.key,
          quantity: line.quantity,
          properties: { ...(line.properties || {}), [saleDisclosureLabel]: wanted },
        });
        changed = true;
      }
      syncCartCount(cart);
      if (changed) window.location.reload();
    } catch (_error) {
      delete root.dataset.ugSaleDisclosureInit;
    }
  };

  const initMobileFilters = () => {
    if (!window.matchMedia("(max-width: 820px)").matches) return;
    document.querySelectorAll(".ug-filters details[open]").forEach((details) => {
      if (!details.querySelector("input:checked")) details.removeAttribute("open");
    });
  };

  const initAnchorTabs = () => {
    document.querySelectorAll('a[href^="#"]').forEach((link) => {
      if (link.dataset.ugAnchorInit === "true") return;
      link.dataset.ugAnchorInit = "true";
      link.addEventListener("click", (event) => {
        const target = document.querySelector(link.getAttribute("href"));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start",
        });
      });
    });
  };

  const init = () => {
    root.classList.remove("no-js");
    root.classList.add("js");
    initHeader();
    initReveal();
    initReviewProof();
    initTilt();
    initModal();
    initQuickAdd();
    initProductForms();
    initBundleForms();
    initCartForm();
    initCartSaleDisclosures();
    initMobileFilters();
    document.querySelectorAll("[data-ug-filter-panel]").forEach((panel) => {
      if (panel.dataset.ugReady) return;
      panel.dataset.ugReady = "true";
      panel.open = !window.matchMedia("(max-width: 820px)").matches;
      window.matchMedia("(max-width: 820px)").addEventListener("change", (event) => { panel.open = !event.matches; });
    });
    document.querySelectorAll('a[href="#UGSizeGuide"]').forEach((link) => {
      if (link.dataset.ugGuideReady) return;
      link.dataset.ugGuideReady = "true";
      link.addEventListener("click", () => { const guide = document.getElementById("UGSizeGuide"); if (guide) guide.open = true; });
    });
    initAnchorTabs();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
